import { isLocalAgentError, readUploadStatus, type LocalAgentClient, type UploadStatus } from '@krp/client-runtime'

import type { WorkplaceToken } from '../../agent-session/model/agentSessionController'

export interface DeliveryReaderDeps {
  client: LocalAgentClient
  /** Порт и установка выбранного агента на момент чтения; `null` — агента нет. */
  agent: () => { port: number; instanceId: string } | null
  fetchToken: (agentInstanceId: string) => Promise<WorkplaceToken>
  clock?: () => number
}

/** Токен берётся заранее, за столько до конца срока обновляется, мс. */
const RENEW_BEFORE_MS = 60_000

/**
 * Одно чтение состояния выгрузки агента (`GET /v1/upload`) для монитора диагностики. Токен рабочего места держится в памяти и
 * переиспользуется (чтение редкое — запрашивать токен у платформы каждую минуту незачем); не принял токен — один раз обновляем.
 * Только чтение: ни одной команды кассе здесь нет.
 */
export function createDeliveryReader(deps: DeliveryReaderDeps): () => Promise<UploadStatus> {
  const clock = deps.clock ?? (() => Date.now())
  let cached: { instanceId: string; value: WorkplaceToken } | null = null

  const tokenFor = async (instanceId: string, renew: boolean): Promise<string> => {
    if (!renew && cached !== null && cached.instanceId === instanceId && cached.value.expiresAt - RENEW_BEFORE_MS > clock()) return cached.value.token
    const value = await deps.fetchToken(instanceId)
    cached = { instanceId, value }
    return value.token
  }

  return async () => {
    const agent = deps.agent()
    if (agent === null) throw new Error('Кассовый агент на этом компьютере не найден.')
    try {
      return await readUploadStatus(deps.client, agent.port, await tokenFor(agent.instanceId, false))
    } catch (reason) {
      if (!isLocalAgentError(reason) || reason.kind !== 'unauthorized') throw reason
      return readUploadStatus(deps.client, agent.port, await tokenFor(agent.instanceId, true))
    }
  }
}
